import { useMemo } from 'react'
import type { CockpitSearchResult } from '@/lib/api'
import { stripTimestamp } from '../lib'

type ExactRow = NonNullable<CockpitSearchResult['exact']>[number]
type OpenFn = (path: string, name?: string, meta?: string) => void

/** Server search results (Enter in the search box). Exact matches are grouped
 * by domain → workstream (the user's mental hierarchy), with the active
 * domain chip narrowing them; then a related-by-meaning pgvector section.
 * Port of the plugin CockpitView's _renderSearchResults, reorganized. */
export function CockpitSearchResults({
  query,
  searching,
  results,
  domain,
  onBack,
  onOpen,
}: {
  query: string
  searching: boolean
  results: CockpitSearchResult | null
  domain: string | null
  onBack: () => void
  onOpen: OpenFn
}) {
  const exact = useMemo(() => {
    const rows = results?.exact || []
    return domain ? rows.filter((r) => r.domain === domain) : rows
  }, [results, domain])

  // Group by "domain / workstream", workstreams alphabetical, blocks before
  // cards inside each group, names alphabetical within a kind.
  const groups = useMemo(() => {
    const by = new Map<string, ExactRow[]>()
    for (const r of exact) {
      const key = `${r.domain}${r.workstream ? ` / ${r.workstream}` : ''}`
      if (!by.has(key)) by.set(key, [])
      by.get(key)!.push(r)
    }
    return [...by.entries()]
      .map(([label, rows]) => ({
        label,
        rows: rows.sort(
          (a, b) =>
            (a.kind === 'block' ? 0 : 1) - (b.kind === 'block' ? 0 : 1) ||
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        ),
      }))
      .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
  }, [exact])

  if (searching) {
    return <div className="text-[12px] text-muted-foreground italic py-4">Searching the vault for “{query}”…</div>
  }

  const sem = results?.semantic || []
  const blockN = exact.filter((r) => r.kind === 'block').length
  const cardN = exact.filter((r) => r.kind === 'card').length

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="text-[11px] px-2 py-1 border border-border text-muted-foreground hover:text-foreground transition-colors"
      >
        ← back to board (Esc)
      </button>
      {(!results || results.ok === false) && (
        <div className="text-[12px] text-red-400 py-3 break-words">Search failed: {results?.error || 'unknown'}</div>
      )}
      {results?.ok !== false && (
        <>
          {exact.length > 0 && (
            <div className="text-[11px] text-muted-foreground mt-4 mb-1">
              {blockN} block{blockN === 1 ? '' : 's'} · {cardN} card{cardN === 1 ? '' : 's'}
              {domain ? ` · in ${domain}` : ''} · grouped by workstream
            </div>
          )}
          {!exact.length && <div className="text-[12px] text-muted-foreground italic py-3">No exact matches{domain ? ` in ${domain}` : ''}.</div>}

          {groups.map((g) => (
            <section key={g.label} className="mt-3">
              <h3 className="text-[12px] text-foreground border-b border-border pb-1 mb-1">{g.label}</h3>
              {g.rows.map((r) => (
                <button
                  key={r.path}
                  type="button"
                  className="w-full text-left px-1.5 py-1.5 hover:bg-white/[0.04] min-w-0 block"
                  onClick={() => onOpen(r.path, r.name, g.label)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="flex-shrink-0 text-[9px] border border-border px-1 text-muted-foreground uppercase">
                      {r.kind}
                    </span>
                    <span className="text-[13px] text-foreground truncate">{stripTimestamp(r.name)}</span>
                  </div>
                  {r.snippet && <div className="text-[11px] text-muted-foreground truncate mt-0.5">…{r.snippet}…</div>}
                </button>
              ))}
            </section>
          ))}

          {sem.length > 0 && (
            <section className="mt-5">
              <h3 className="text-[13px] text-foreground mb-1.5">Related by meaning</h3>
              {sem.map((r, i) => {
                const label = r.block_name || (r.path || '').split('/').pop() || '?'
                const meta = `${r.universe || ''}${r.project ? ` / ${r.project}` : ''}`
                return (
                  <button
                    key={`${r.path}-${i}`}
                    type="button"
                    className="w-full text-left px-1.5 py-1.5 hover:bg-white/[0.04] min-w-0 block"
                    onClick={() => onOpen(r.path, label, meta)}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[13px] text-foreground truncate">{stripTimestamp(label)}</span>
                      <span className="ml-auto flex-shrink-0 text-[11px] text-muted-foreground">
                        {meta} · {Math.round((r.similarity || 0) * 100)}%
                      </span>
                    </div>
                    {r.excerpt && <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{r.excerpt.slice(0, 220)}</div>}
                  </button>
                )
              })}
            </section>
          )}
          {results?.took_ms != null && <div className="text-[10px] text-muted-foreground mt-3">{results.took_ms}ms</div>}
        </>
      )}
    </div>
  )
}
